import { ref } from "vue"
import { defineStore, storeToRefs } from "pinia"
import type { Answers, TItem } from "@/features/swipe/store/swipe.types.js"
import {
  initAnswers,
  initExtra,
  factoryTest,
} from "@/features/swipe/store/swipe.data.js"
import { useToast } from "vue-toastification"
import { useI18nStore } from "@/stores/i18n"
import { useUserStore } from "@/stores/user"
import {
  patchSwipe,
  getSwipeAssessment,
  getSwipeActive,
  postSwipe,
  deleteSwipe,
} from "@/api/viktor/assessment/assessment"
import { getVersionFromLang } from "@/features/swipe/helpers/getVersionFromLang"
import { useQuestionsStore } from "@/features/swipe/store/questions.store"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { swipeActor } from "../machines/swipeMachine"
import { sleep } from "@/lib/utils"
import Bugsnag from "@bugsnag/js"

export const useAnswersStore = defineStore("swipeAnswersStore", () => {
  const { setCurrentIndex } = useQuestionsStore()
  const i18nStore = useI18nStore()
  const { updateLocale, getLocaleIdFromIsoCode } = i18nStore
  const { currentLocale } = storeToRefs(useI18nStore())
  const { user, isRecruiter } = storeToRefs(useUserStore())

  const answers = ref<Answers>(initAnswers)
  const isPatchExtraLoading = ref(false)

  const userStore = useUserStore()
  const { isFromInt } = storeToRefs(useUserStore())

  const toast = useToast()

  //-- FETCH AND SET SWIPE

  async function fetchSwipe() {
    const swipeAssessment = userStore.assessmentSummary.assessments.swipe
    const shapeAssessment = userStore.assessmentSummary.assessments.shape

    if (
      swipeAssessment?.status === "finish" &&
      !swipeAssessment?.can_restart &&
      !swipeAssessment?.retake_test &&
      !swipeAssessment.not_passed
    ) {
      // si le test est terminé, on sort de l'assessment
      if (isFromInt.value) answers.value.status = "thank-you"
      else window.location.reload()
    } else if (
      swipeAssessment?.status === null ||
      shapeAssessment?.status === null ||
      (shapeAssessment?.status === "finish" &&
        (shapeAssessment?.retake_test || shapeAssessment?.can_restart))
    ) {
      // si le test n'a jamais été commencé on l'initalise
      await initSwipe(false, null)
    } else if (
      // si le status est finish et qu'on est soit sur une demande de retake test pour PE
      // ou sur un test datant de plus d'un an
      swipeAssessment?.status === "finish" &&
      (swipeAssessment?.retake_test || swipeAssessment?.can_restart)
    ) {
      // si on a une demande de retake_test
      await deleteSwipe()
      await initSwipe(false, null)
    } else if (
      swipeAssessment?.status === "todo" ||
      swipeAssessment?.status === "inprogress" ||
      !!swipeAssessment?.not_passed
    ) {
      // autrement c'est que le test est en cours
      // donc on récupère les données du test et on popule les stores
      const assessment = await getSwipeActive()

      answers.value = {
        ...initAnswers,
        ...assessment,
        data: {
          ...initAnswers.data,
          ...(typeof assessment.data === "object" &&
          !Array.isArray(assessment.data)
            ? assessment.data
            : {}),
        },
      }
      if (Object.keys(answers.value?.data || {}).length === 0) {
        setCurrentIndex(0)
        answers.value.data.extra = initAnswers.data.extra
      } else {
        setCurrentIndex(answers.value?.data?.items?.length)
      }

      // sometimes version was not set in the assessment
      let versionFull
      if (answers.value?.version === null) {
        versionFull = getVersionFromLang(currentLocale.value.iso_code)
        answers.value.version = versionFull
      } else {
        versionFull = answers.value.version
      }

      const lastUnderscoreIndex = versionFull.lastIndexOf("_")
      const langCode = versionFull.substring(0, lastUnderscoreIndex)
      userStore.audio = answers.value.data?.extra?.general_audio ? true : false
      answers.value.data.extra.elapsed_time = assessment.elapsed_time
      answers.value.data.extra.finished = assessment.finished_at ? true : false
      answers.value.data.extra.native_language = assessment.native_language
      answers.value.data.extra.wholesome_conditions =
        assessment.wholesome_conditions

      if (
        langCode !== currentLocale.value.iso_code &&
        answers.value?.status === "inprogress"
      ) {
        const locale_id = getLocaleIdFromIsoCode(langCode)
        await updateLocale(locale_id)
        // @TODO: set trad key from luciano
        toast.info("Language change")
      }
    }

    return answers.value
  }

  function getActiveAnswer() {
    return answers.value.data.extra.current || 0
  }

  async function answerQuestion(id: number, answer: TItem) {
    if (answers.value.data.items.length < 76) {
      answers.value.data.items[id] = answer
    } else {
      console.warn("too many answers")
      return
    }
  }

  //-- TRIAL

  /**
   * Patch trial
   * @param id
   * @param data
   */
  async function patchTrial(
    id: number,
    data: { time: number; errors: number }
  ) {
    const step = `step_${id + 1}` as "step_1" | "step_2" | "step_3"

    if (!answers.value.data.trial) {
      answers.value.data.trial = {
        step_1: { time: 0, errors: 0 },
        step_2: { time: 0, errors: 0 },
        step_3: { time: 0, errors: 0 },
        retries: 0,
        finished: false,
      }
    }

    answers.value.data.trial[step] = data
    await patchSwipeData()
  }

  /**
   * Finish trial
   */
  async function finishTrial() {
    if (answers.value.data.trial) answers.value.data.trial.finished = true
    await patchSwipeData()
    MixpanelInstance.track("swipe_start")
  }

  /**
   * Restart trial
   */
  async function restartTrial() {
    answers.value.data.trial = {
      step_1: { time: 0, errors: 0 },
      step_2: { time: 0, errors: 0 },
      step_3: { time: 0, errors: 0 },
      retries: (answers.value.data.trial?.retries ?? 0) + 1,
      finished: false,
    }
    await patchSwipeData()
  }

  //-- TEST

  /**
   * Current item
   * @param id
   */
  async function setCurrentItem(id: number) {
    answers.value.data.extra.current = id
  }

  /**
   * Swipe back
   * @param id
   * @param answer
   */
  async function swipeBack(id: number, answer: TItem) {
    answers.value.data.items[id] = answer
    await patchSwipeData()
  }

  /**
   * Answer a question
   */
  async function patchSwipeData() {
    try {
      const data = {
        version: getVersionFromLang(currentLocale.value.iso_code),
        is_completed: answers.value.is_completed,
        locale_id: currentLocale.value.id,
        status: answers.value.status,
        data: answers.value.data,
      }
      await patchSwipe(data)
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.context = "patchSwipeData in answers store"
        event.severity = "error"
        event.unhandled = true
        event.addMetadata("request", {
          endpoint: "patchSwipe",
          dataSize: JSON.stringify(answers.value?.data).length,
        })
        event.addMetadata("full_data", {
          answersData: JSON.stringify(answers.value?.data),
        })
      })
    }
  }

  async function patchTutorial(id: number, val: number) {
    const step = `step_${id + 1}`

    if (!answers.value.data.tutorial_time) {
      answers.value.data.tutorial_time = {
        step_1: 0,
        step_2: 0,
        step_3: 0,
        step_4: 0,
        finished: false,
      }
    }

    answers.value.data.tutorial_time[step] =
      answers.value.data.tutorial_time[step] + val
    await patchSwipeData()
  }

  async function patchExtra(type: string, answer: boolean) {
    isPatchExtraLoading.value = true
    try {
      answers.value.data.extra[type] = answer
      if (type === "wholesome_conditions") {
        answers.value.data.extra.elapsed_time = getElapsedTime()
        // si langue natale + conditions de wholesome sont true, c'est la fin du test, pas de restart
        if (answers.value.data.extra.native_language && answer) {
          answers.value.data.extra.isRestarting = false
          await isTheEnd()
        } else if (answers.value.data.extra.isRestarting) {
          await isTheEnd()
        } else if (userStore.isFromInt.value) {
          await isTheEnd()
        }
      }
      await patchSwipeData()
    } finally {
      await sleep(300) // Timeout to prevent flicker
      isPatchExtraLoading.value = false
    }
  }

  async function finishTutorial() {
    if (!answers.value.data.tutorial_time) {
      answers.value.data.tutorial_time = {
        step_1: 0,
        step_2: 0,
        step_3: 0,
        step_4: 0,
        finished: false,
      }
    }

    answers.value.data.tutorial_time.finished = true
    await patchSwipeData()
  }

  async function finishQuestions() {
    answers.value.data.extra.all_items_completed = true
    await patchSwipeData()
    swipeActor.send({ type: "done" })
  }

  async function isTheEnd() {
    answers.value.data.extra.finished = true
    answers.value.status = "finish"

    MixpanelInstance.track("swipe_finish", {
      locale: answers.value.locale_id,
      isRecruiter: isRecruiter.value,
      origin: isRecruiter.value ? "invited" : "autonomous",
      data: answers.value.data,
    })
  }

  function getElapsedTime() {
    let time = 0
    answers.value.data.items.map((item) => (time += item.time_initial))
    return Math.round(time)
  }

  async function doublecheckShapeOrSwipe() {
    let alreadyPassedSwipe = false

    const swipe = userStore.assessmentSummary.assessments.swipe
    if (swipe && !swipe.status) {
      const getSwipe = await getSwipeAssessment()
      alreadyPassedSwipe = Array.isArray(getSwipe)
        ? getSwipe.length > 0
        : Object.keys(getSwipe).length > 0
    }

    return alreadyPassedSwipe
  }

  // ACTIONS
  async function initSwipe(restarting?: boolean, lang?: number | null) {
    const payload: Answers = {
      ...initAnswers,
      version: getVersionFromLang(currentLocale.value.iso_code),
      locale_id: lang ?? currentLocale.value.id,
      is_active: answers.value.is_active,
      status: answers.value.status,
      is_completed: answers.value.is_completed,
      user: {
        uuid: user.value.user.uuid,
        first_name: user.value.user.first_name,
        email: user.value.user.email,
        is_recruiter: user.value.user.company_uuid !== null,
        is_guest: user.value.permissions?.includes("int"),
        gender: user.value.user.gender,
      },
      data: {
        ...initAnswers.data,
        extra: {
          ...initExtra,
          isRestarting: restarting ?? null,
        },
      },
      permissions: user.value.permissions,
    }
    const data = await postSwipe(payload)
    answers.value = payload
    setCurrentIndex(0)

    return data
  }

  //-- RESTART

  async function patchRestart(
    type: string,
    answer: boolean,
    lang?: number | null
  ) {
    // try to submit final results
    try {
      answers.value.data.extra.isRestarting = answer
      await isTheEnd()
      await patchSwipeData()
    } catch (error) {
      console.log(error)
      return
    }

    // then, if user wants to restart
    if (answer) {
      lang && (await updateLocale(lang))
      await resetUserData(true)

      // finally, send user to the right state (now or later)
      if (type === "now") {
        return swipeActor.send({ type: "now" })
      } else {
        return swipeActor.send({ type: "later" })
      }
    } else {
      return swipeActor.send({ type: "done" })
    }
  }

  async function resetUserData(isRestarting: boolean | null) {
    await deleteSwipe()

    // restart to save the fact that the user has already passed the test
    // reload : on restart screen, user can choose to restart later so no reloading
    console.log(currentLocale.value)
    const cleanData = payloadToResetUserData(
      getVersionFromLang(currentLocale.value.iso_code),
      currentLocale.value.id,
      isRestarting
    )
    answers.value.version = cleanData.version
    answers.value.locale_id = cleanData.locale_id
    answers.value.data = cleanData.data
    console.log(cleanData)
    await postSwipe(cleanData)
  }

  const payloadToResetUserData = (
    version: string,
    locale_id: number,
    isRestarting: boolean | null
  ) => {
    return {
      version: version,
      locale_id: locale_id,
      data: {
        tutorial_time: {
          step_1: 0,
          step_2: 0,
          step_3: 0,
          step_4: 0,
          finished: false,
        },
        trial: {
          step_1: { time: 0, errors: 0 },
          step_2: { time: 0, errors: 0 },
          step_3: { time: 0, errors: 0 },
          retries: 0,
          finished: false,
        },
        items: [],
        extra: {
          all_items_completed: false,
          current: 0,
          native_language: null,
          wholesome_conditions: null,
          elapsed_time: 0,
          finished: false,
          isRestarting: isRestarting,
          trigger_events: true,
        },
      },
    }
  }

  async function resetUserDataLight() {
    const payload = payloadToResetUserDataLight(currentLocale.value.id)
    await patchSwipe(payload)
    window.location.reload()
  }

  const payloadToResetUserDataLight = (locale_id: number) => {
    return {
      version: "fr_FR_1.0.0",
      locale_id: locale_id,
      data: {
        tutorial_time: {
          step_1: 0,
          step_2: 0,
          step_3: 0,
          step_4: 0,
          finished: false,
        },
        trial: {
          step_1: { time: 0, errors: 0 },
          step_2: { time: 0, errors: 0 },
          step_3: { time: 0, errors: 0 },
          retries: 0,
          finished: false,
        },
        items: [],
        extra: {
          all_items_completed: false,
          current: 0,
          native_language: null,
          wholesome_conditions: null,
          elapsed_time: 0,
          finished: false,
          isRestarting: null,
          trigger_events: true,
        },
      },
    }
  }

  async function submitFinalResults(lang?: number | null) {
    try {
      answers.value.data.extra.elapsed_time = getElapsedTime()
      answers.value.data.extra.finished = true
      const payload: Answers = {
        version: answers.value.version,
        locale_id: lang || currentLocale.value.id,
        data: answers.value.data,
      }

      // ultime patch to finish the test
      await patchSwipe(payload)

      MixpanelInstance.track("swipe_finish", {
        payload: payload,
      })

      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  async function patchFactoryTest(isRestarting: boolean) {
    const payload = factoryTest(currentLocale.value.id, isRestarting)
    await patchSwipe(payload)
    window.location.reload()
  }

  return {
    answers,
    fetchSwipe,
    getActiveAnswer,
    doublecheckShapeOrSwipe,
    answerQuestion,
    patchSwipeData,
    resetUserData,
    resetUserDataLight,
    swipeBack,
    patchTutorial,
    patchTrial,
    finishTrial,
    restartTrial,
    finishTutorial,
    finishQuestions,
    setCurrentItem,
    patchExtra,
    isPatchExtraLoading,
    patchRestart,
    submitFinalResults,
    patchFactoryTest,
  }
})
